import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Accordion, Icon } from 'semantic-ui-react';
import FieldRow from '../FieldRow';
import { getActionOnFailLabel } from '../fieldsConfig';

const GroupedRuleList = ({
  rules,
  onIfNullChange,
  upsertNote,
  selectedProduct,
  onRemoveRule,
}) => {
  const groupedRules = useMemo(() => {
    if (!rules || !rules.length) return [];
    const groups = {};
    rules.forEach(rule => {
      const key = rule.actionOnFail || 'UNKNOWN';
      if (!groups[key]) groups[key] = [];
      groups[key].push(rule);
    });
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === 'DENY') return -1;
      if (b === 'DENY') return 1;
      return 0;
    });
  }, [rules]);

  const [activeIndices, setActiveIndices] = useState([]);

  // Key on group identity, not the memoized array: editing a rule value
  // rebuilds `groupedRules` and would re-open panels the user had collapsed.
  const groupKeys = groupedRules.map(([actionOnFail]) => actionOnFail).join('|');
  const groupCount = groupedRules.length;

  // Open every panel when the set of groups changes.
  useEffect(() => {
    setActiveIndices(Array.from({ length: groupCount }, (_, i) => i));
  }, [groupKeys, groupCount]);

  const handleAccordionClick = (e, { index }) => {
    setActiveIndices(prev => (prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]));
  };

  return (
    <Accordion fluid styled data-testid='rules-accordion'>
      {groupedRules.map(([actionOnFail, groupRules], index) => (
        <React.Fragment key={actionOnFail}>
          <Accordion.Title
            active={activeIndices.includes(index)}
            index={index}
            onClick={handleAccordionClick}
            data-testid={`accordion-title-${actionOnFail}`}
          >
            <Icon name='dropdown' />
            {getActionOnFailLabel(actionOnFail)}{' '}
            <span
              className='accordion-rule-count'
              data-testid={`accordion-rule-count-${actionOnFail}`}
            >
              {groupRules.length}
            </span>
          </Accordion.Title>
          <Accordion.Content
            active={activeIndices.includes(index)}
            data-testid={`accordion-content-${actionOnFail}`}
          >
            {groupRules.map(rule => (
              <FieldRow
                key={rule.instanceId}
                rule={rule}
                onIfNullChange={onIfNullChange}
                upsertNote={upsertNote}
                selectedProduct={selectedProduct}
                onRemoveRule={onRemoveRule}
              />
            ))}
          </Accordion.Content>
        </React.Fragment>
      ))}
    </Accordion>
  );
};

GroupedRuleList.propTypes = {
  rules: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      actionOnFail: PropTypes.string,
    })
  ).isRequired,
  onIfNullChange: PropTypes.func.isRequired,
  upsertNote: PropTypes.func.isRequired,
  selectedProduct: PropTypes.string.isRequired,
  onRemoveRule: PropTypes.func.isRequired,
};

export default GroupedRuleList;
